import { findAnimationById } from 'Pods/ThreeDStudio/Services/Constants/animations'
import { openedModelToStatePatch } from 'Pods/ThreeDStudio/Services/openedModelToStatePatch/openedModelToStatePatch'
import { PROVIDER_LABELS } from 'Pods/ThreeDStudio/Shared/AIEngineSelector/providerLabels'
import {
  ACTION,
  FACE_LEVEL,
  MATERIAL,
  POLYGON_TYPE,
  TEXTURE_QUALITY,
} from 'Pods/ThreeDStudio/Store/constants'

const POLYGON_TYPE_KEYS = {
  [POLYGON_TYPE.QUADRILATERAL]: 'shared.polygonType.quad',
  [POLYGON_TYPE.TRIANGLE]: 'shared.polygonType.triangular',
}

const FACE_LEVEL_KEYS = {
  [FACE_LEVEL.LOW]: 'shared.polyCount.low',
  [FACE_LEVEL.MEDIUM]: 'shared.polyCount.medium',
  [FACE_LEVEL.HIGH]: 'shared.polyCount.high',
}

const MATERIAL_KEYS = {
  [MATERIAL.PBR]: 'leftSide.material.pbr',
  [MATERIAL.BASIC]: 'leftSide.material.basic',
  [MATERIAL.GEOMETRY_ONLY]: 'leftSide.material.geometryOnly',
}

const TEXTURE_QUALITY_KEYS = {
  [TEXTURE_QUALITY.TWO_K]: 'viewerSidebar.enhance.texture.resolution.2k',
  [TEXTURE_QUALITY.FOUR_K]: 'viewerSidebar.enhance.texture.resolution.4k',
  [TEXTURE_QUALITY.EIGHT_K]: 'viewerSidebar.enhance.texture.resolution.8k',
}

const translateFrom = (t, keys, value) => (keys[value] ? t(keys[value]) : null)

const formatFaces = (value) => {
  const faces = Number(value)
  return value != null && value !== '' && faces > 0 ? faces.toLocaleString() : null
}

const engineDetail = (t, provider) => ({ label: t('shared.aiEngine.label'), value: PROVIDER_LABELS[provider] })

const facesDetail = (t, targetPolycount) => {
  const faces = formatFaces(targetPolycount)
  return {
    label: t('viewer.modelDetails.faces'),
    value: faces,
    shortValue: faces && t('viewerSidebar.enhance.variations.info.faces', { faces }),
  }
}

const promptDetail = (t, prompt) => ({
  label: t('viewerSidebar.enhance.variations.info.prompt'),
  value: prompt ? `“${prompt}”` : null,
})

const materialDetail = (t, material) => ({
  label: t('leftSide.material.label'),
  value: translateFrom(t, MATERIAL_KEYS, material),
})

const polygonTypeDetail = (t, polygonType) => ({
  label: t('shared.polygonType.label'),
  value: translateFrom(t, POLYGON_TYPE_KEYS, polygonType),
})

const buildDetails = (t, patch) => {
  const {
    schema = {},
    modelSettings = {},
    remeshSettings = {},
    textureSettings = {},
    animateSettings = {},
  } = patch

  switch (schema.enhancedAction) {
    case undefined:
    case null:
      return [
        engineDetail(t, schema.generationProvider),
        facesDetail(t, modelSettings.targetPolycount),
        polygonTypeDetail(t, modelSettings.polygonType),
        materialDetail(t, modelSettings.material),
        promptDetail(t, modelSettings.prompt),
      ]
    case ACTION.MESH_SIMPLIFICATION:
      return [
        polygonTypeDetail(t, remeshSettings.polygonType),
        { label: t('shared.polyCount.label'), value: translateFrom(t, FACE_LEVEL_KEYS, remeshSettings.faceLevel) },
        engineDetail(t, schema.enhancedProvider),
        facesDetail(t, remeshSettings.targetPolycount),
      ]
    case ACTION.RETEXTURE:
      return [
        engineDetail(t, schema.enhancedProvider),
        promptDetail(t, textureSettings.prompt),
        materialDetail(t, textureSettings.material),
        {
          label: t('viewerSidebar.enhance.texture.resolution.label'),
          value: translateFrom(t, TEXTURE_QUALITY_KEYS, textureSettings.textureQuality),
        },
      ]
    case ACTION.MESH_SEGMENTATION:
      return [engineDetail(t, schema.enhancedProvider)]
    case ACTION.ANIMATION:
      return [
        {
          label: t('viewerSidebar.enhance.variations.info.animation'),
          value: findAnimationById(animateSettings.animationId)?.name,
        },
        engineDetail(t, schema.enhancedProvider),
      ]
    default:
      return []
  }
}

/**
 * Lists the settings that produced a variation, most relevant first, so the card
 * can show the first ones inline and the rest in its tooltip.
 * @param {Object} variation - Run from useGetVariations
 * @param {Function} t - Translation function
 * @returns {Array<{ label: string, value: string, shortValue?: string }>}
 */
export const getVariationDetails = (variation, t) => {
  const patch = openedModelToStatePatch(variation)
  if (!patch.schema) {
    return []
  }

  return buildDetails(t, patch).filter(detail => detail.value)
}
